import { BasicType, AdvancedType } from '@core/constants';
import { IBlock, IBlockData } from '@core/typings';
import { createCustomBlock } from '@core/utils/createCustomBlock';
import {
	TemplateEngineManager,
	applyDynamicColorVarsToHtml,
	createBlock,
	formatDynamicTextValue,
	getAncestorPinnedListing,
	parseDynamicTextHelperExpression,
	resolveBoundListing,
	resolveMergeTagValue,
	resolvePreviewColor,
	t,
	BlockManager,
	getParentByIdx,
	CUSTOM_LISTING_BLOCK_TYPE,
} from '@core/utils';
import {
	getPreviewResolutionDataSource,
	resolveAttachedListingMergeTags,
} from '@core/utils/resolveAttachedListingMergeTags';
import { merge } from 'lodash';
import React from 'react';
import { IPage, standardBlocks } from '../standard';
import { BasicBlock } from '@core/components/BasicBlock';
import { Column, Section } from '@core/components';

const MERGE_TAG_PATTERN =
	/(\{\{\{\{raw\}\}\}\})?\{\{\s*([^{}]+?)\s*\}\}(\{\{\{\{\/raw\}\}\}\})?/gi;
const IF_MERGE_TAG_PATTERN =
	/\{\{#if\s+([\w.-]+)\}\}\s*\{\{([^{}]+?)\}\}\s*\{\{else\}\}([\s\S]*?)\{\{\/if\}\}/g;

function resolveDataSourceMergeTags(
	template: string | undefined,
	dataSource?: Record<string, any>,
) {
	if (!template || !dataSource) return template || '';

	const getFormattedResolvedValue = (
		resolvedValue: unknown,
		resolvedFormat?: string,
		resolvedMaxLength?: number | null,
	): string | null => {
		if (
			typeof resolvedValue === 'string' ||
			typeof resolvedValue === 'number' ||
			typeof resolvedValue === 'boolean'
		) {
			return formatDynamicTextValue(resolvedValue, resolvedFormat, resolvedMaxLength);
		}

		if (Array.isArray(resolvedValue)) {
			const firstValue = resolvedValue.find(
				item =>
					typeof item === 'string' ||
					typeof item === 'number' ||
					typeof item === 'boolean',
			);
			if (firstValue !== undefined) {
				return formatDynamicTextValue(firstValue, resolvedFormat, resolvedMaxLength);
			}
		}

		return null;
	};

	const withConditionalsResolved = template.replace(
		IF_MERGE_TAG_PATTERN,
		(_, conditionKey, trueExpression, fallbackText) => {
			const fallback = String(fallbackText || '').trim();
			const parsedHelper = parseDynamicTextHelperExpression(
				String(trueExpression || '').trim(),
			);
			const actualKey = String(parsedHelper?.key || conditionKey || '').trim();
			if (!actualKey) return fallback;

			const resolvedValue = resolveMergeTagValue(dataSource, actualKey);
			const formattedValue = getFormattedResolvedValue(
				resolvedValue,
				parsedHelper?.format,
				parsedHelper?.maxLength,
			);

			if (formattedValue !== null && formattedValue.trim() !== '') {
				return formattedValue;
			}

			return fallback;
		},
	);

	return withConditionalsResolved.replace(MERGE_TAG_PATTERN, match => {
		const keyMatch = match.match(/\{\{\s*([^{}]+?)\s*\}\}/);
		const rawKey = String(keyMatch?.[1] || '');
		const trimmedRawKey = rawKey.trim();

		// Skip orphaned Handlebars block tokens
		if (
			trimmedRawKey.startsWith('#') ||
			trimmedRawKey.startsWith('/') ||
			trimmedRawKey === 'else'
		)
			return '';

		const parsedHelper = parseDynamicTextHelperExpression(trimmedRawKey);
		const pipeIdx = trimmedRawKey.indexOf('|');
		const actualKey = parsedHelper
			? parsedHelper.key
			: pipeIdx >= 0
				? trimmedRawKey.slice(0, pipeIdx).trim()
				: trimmedRawKey;
		const fallbackText =
			!parsedHelper && pipeIdx >= 0 ? trimmedRawKey.slice(pipeIdx + 1).trim() : '';

		const resolvedValue = resolveMergeTagValue(dataSource, actualKey);
		const formattedValue = getFormattedResolvedValue(
			resolvedValue,
			parsedHelper?.format,
			parsedHelper?.maxLength,
		);

		// A genuinely blank resolved value (a real field that exists but is empty)
		// is not a resolved value — fall through to the tag's own fallback text,
		// or the raw tag, same as an unresolved one. Never render nothing.
		if (formattedValue !== null && formattedValue.trim() !== '') {
			return formattedValue;
		}

		if (fallbackText) return fallbackText;
		return match;
	});
}

export function generateAdvancedTableBlock(option: {
	type: string;
	baseType: BasicType;
}) {
	return createCustomBlock<AdvancedTableBlock>({
		get name() {
			return t('Table');
		},
		type: option.type,
		validParentType: [
			BasicType.PAGE,
			BasicType.WRAPPER,
			BasicType.SECTION,
			BasicType.COLUMN,
			BasicType.GROUP,
			BasicType.HERO,
			AdvancedType.WRAPPER,
			AdvancedType.SECTION,
			AdvancedType.COLUMN,
			AdvancedType.GROUP,
			AdvancedType.HERO,
		],
		create: payload => {
			const defaultData: AdvancedTableBlock = {
				type: option.type,
				data: {
					value: {
						tableSource: [
							[
								{ content: '<b>Column 1</b>' },
								{ content: '<b>Column 2</b>' },
								{ content: '<b>Column 3</b>' },
							],
							[
								{ content: 'Item 1' },
								{ content: 'Description' },
								{ content: 'More Info' },
							],
							[
								{ content: 'Item 2' },
								{ content: 'Description' },
								{ content: 'More Info' },
							],
						],
					},
				},
				attributes: {
					cellBorderColor: '#000000',
					cellPadding: '8px',
					align: 'center',
					padding: '0px 0px 0px 0px',
					'border-width': '0px',
					'border-style': 'solid',
					'border-color': '#000000',
					color: '#000000',
					'container-background-color': '#FFFFFF',
					'font-size': '14px',
					'font-family': 'Arial, sans-serif',
					'line-height': '1.7',
				},
				children: [],
			};
			return merge(defaultData, payload);
		},
		render: params => {
			const { data, idx, context } = params;
			const { cellPadding } = data.attributes;
			const textAlign = data.attributes['align'];
			const fontStyle = data.attributes['font-style'];
			const isPreviewMode = Boolean(params.dataSource?.previewMode);
			// Not in rasterMode: nothing there can resolve a user.* tag, so resolving
			// could only swap the tag for its send-time fallback — losing the token the
			// raster copy needs to print a field name and carry the underline.
			const shouldResolveMergeTags =
				params.mode === 'testing' && isPreviewMode && !params.dataSource?.rasterMode;
			// This block offers a listing picker, so it has to honour one — its own
			// selection first, else the listing pinned by an ancestor Listing Wrapper
			// or listing column. Without this the picker accepted a listing and the
			// table ignored it, and a table inside a listing scope never hydrated.
			// An enclosing Listing Wrapper / listing column WINS — see the Text block.
			const selectedListing =
				getAncestorPinnedListing(context, idx) ||
				resolveBoundListing(data.data.value);
			// Resolve dynamic brand-colour merge tags to hex in preview; keep the raw
			// tag in production (resolved at send time).
			const cellBorderColor = resolvePreviewColor(
				data.attributes.cellBorderColor,
				params.mode,
				params.dataSource,
			);
			// Table-level default cell background applied to every cell; a per-cell
			// backgroundColor (set on an individual cell) still wins.
			const cellBackgroundColor = resolvePreviewColor(
				data.attributes.cellBackgroundColor,
				params.mode,
				params.dataSource,
			);

			const content = data.data.value.tableSource
				.map((tr, index) => {
					const styles = [] as any[];
					if (cellPadding) {
						styles.push(`padding: ${cellPadding}`);
					}
					if (cellBorderColor) {
						styles.push(`border: 1px solid ${cellBorderColor}`);
					}
					const _trString = tr.map(
						e => {
							// Per-cell background wins over the table-level default.
							const resolvedCellBackground =
								resolvePreviewColor(
									e.backgroundColor,
									params.mode,
									params.dataSource,
								) || cellBackgroundColor;
							const backgroundStyle = resolvedCellBackground
								? ` background-color:${resolvedCellBackground};`
								: '';
							// Re-derive any RTE dynamic brand colours in the cell's rich text from
							// their merge-tag key: current brand hex in testing, `{{key}}` in
							// production (same contract as the Text block).
							// Listing tags resolve first, then the preview data source fills
							// whatever is left — same order as the Text block.
		// TODO(raster): a listing tag here still resolves to bare text in the raster
		// copy and loses its underline — cells do not pass the merge-field wrapper
		// the Text block does. Add it when this block's raster output matters.
							const listingResolvedCell = resolveAttachedListingMergeTags(e.content, {
								listing: selectedListing,
							});
							const cellContent = applyDynamicColorVarsToHtml(
								shouldResolveMergeTags
									? resolveDataSourceMergeTags(
											listingResolvedCell,
											getPreviewResolutionDataSource(params.dataSource, selectedListing),
										)
									: listingResolvedCell,
								params.mode,
								params.dataSource,
							);
							return `<td rowspan="${e.rowSpan || 1}" colspan="${
								e.colSpan || 1
							}" style="${styles.join(';')};${backgroundStyle}">${cellContent}</td>`;
						},
					);
					return `<tr style="text-align:${textAlign};font-style:${fontStyle};">${_trString.join(
						'\n',
					)}</tr>`;
				})
				.join('\n');

			// Combine separate border attributes into single border attribute
			const borderWidth = data.attributes['border-width'];
			const borderStyle = data.attributes['border-style'];
			const borderColor = resolvePreviewColor(
				data.attributes['border-color'],
				params.mode,
				params.dataSource,
			);

			// Create derived attributes (no mutation)
			let attributes = data.attributes;

			if (borderWidth || borderStyle || borderColor) {
				const combinedBorder = [
					borderWidth || '0px',
					borderStyle || 'solid',
					borderColor || '#000000',
				].join(' ');

				attributes = {
					...data.attributes,
					border: combinedBorder,
				};
			}

			const children = (
				<BasicBlock
					params={{
						...params,
						data: {
							...data,
							attributes,
						},
					}}
					tag='mj-table'
				>
					{content}
				</BasicBlock>
			);

			// Auto-wrapper logic: an mj-table is content, so it needs a column, and a
			// column needs a section. Supply whichever are missing based on what the
			// table actually sits in — a bare mj-table under a wrapper or section is
			// markup MJML mangles, and the canvas can no longer select or edit it.
			const parentBlockData = getParentByIdx({ content: context! }, idx!);
			const parentType = parentBlockData?.type;

			// Holds sections. The Listing Wrapper is one of these — it renders
			// mj-wrapper, and leaving it out is what made a table dropped into one
			// render dead.
			if (
				parentType === BasicType.PAGE ||
				parentType === BasicType.WRAPPER ||
				parentType === AdvancedType.WRAPPER ||
				parentType === CUSTOM_LISTING_BLOCK_TYPE
			) {
				return (
					<Section
						padding='0px'
						text-align='left'
					>
						<Column>{children}</Column>
					</Section>
				);
			}

			// Holds columns — supply just the column.
			if (parentType === BasicType.SECTION || parentType === AdvancedType.SECTION) {
				return <Column>{children}</Column>;
			}

			return children;
		},
	});
}

export interface IAdvancedTableData {
	content: string;
	colSpan?: number;
	rowSpan?: number;
	backgroundColor?: string;
}

export type AdvancedTableBlock = IBlockData<
	{
		cellPadding?: string;
		cellBorderColor?: string;
		cellBackgroundColor?: string;
		'font-style'?: string;
		align?: string;
		padding?: string;
		border?: string;
		'border-width'?: string;
		'border-style'?: string;
		'border-color'?: string;
		color?: string;
		'container-background-color'?: string;
		'font-size'?: string;
		'font-family'?: string;
		'line-height'?: string;
	},
	{
		content?: string;
		tableSource: IAdvancedTableData[][];
	}
>;
